import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_NINJA } from '../../settings';
import { StatusEnum } from '../common-types';

export interface IClient {
  id: number;
  owner: number;
  name: number | string;
  description: string;
  project_set: Array<number>;
  is_active: boolean;
  keywords_cnt: number;
  keywords_active_cnt: number;
}

export interface INewClient {
  owner: number;
  name: number | string;
  description: string;
  is_active: boolean;
}

export interface IUpdateClient {
  id: number;
  owner?: number;
  name?: number | string;
  description?: string;
  is_active?: boolean;
}

export interface IClientsState {
  clients: IClient[];
  currentClient: IClient | null;
  status: StatusEnum;
  statusText: string | null;
}

const initialState: IClientsState = {
  clients: [],
  currentClient: null,
  status: StatusEnum.idle,
  statusText: null,
};

export const getClients = createAsyncThunk(
  'organization/getClients',
  async (args: { slug: string }) => {
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/clients`
    );
    return response.data as IClient[];
  }
);

export const getClient = createAsyncThunk(
  'organization/getClient',
  async (args: { slug: string; clientId: number }) => {
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/clients/${args.clientId}`
    );
    return response.data as IClient;
  }
);

export const createClient = createAsyncThunk(
  'organization/createClient',
  async (args: { slug: string; client: INewClient }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const response = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/clients`,
      args.client,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return response.data as IClient;
  }
);

export const updateClient = createAsyncThunk(
  'organization/updateClient',
  async (args: { slug: string; client: IUpdateClient }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/clients/${args.client.id}`,
      args.client,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return response.data as IClient;
  }
);

export const deleteClient = createAsyncThunk(
  'organization/deleteClient',
  async (args: { slug: string; clientId: number }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/clients/${args.clientId}`,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return args.clientId;
  }
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setClient: (state, action: PayloadAction<IClient>) => {
      state.currentClient = action.payload;
    },
    setClientIdle: (state) => {
      state.clients = [];
      state.currentClient = null;
      state.status = StatusEnum.idle;
      state.statusText = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClients.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getClients.fulfilled,
        (state, action: PayloadAction<IClient[]>) => {
          state.status = StatusEnum.succeeded;
          state.clients = [...action.payload].reverse();
          if (!state.currentClient) {
            state.currentClient = action.payload[0] ?? null;
          }
        }
      )
      .addCase(getClients.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(getClient.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(getClient.fulfilled, (state, action: PayloadAction<IClient>) => {
        state.status = StatusEnum.succeeded;
        state.currentClient = action.payload;
      })
      .addCase(getClient.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(createClient.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        createClient.fulfilled,
        (state, action: PayloadAction<IClient>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Клиент создан';
          state.currentClient = action.payload;
          state.clients.unshift(action.payload);
          toast.success(state.statusText);
        }
      )
      .addCase(createClient.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateClient.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateClient.fulfilled,
        (state, action: PayloadAction<IClient>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Клиент обновлен';
          const updatedClientIndex = state.clients.findIndex(
            (c) => c.id === action.payload.id
          );
          if (updatedClientIndex !== -1) {
            state.clients[updatedClientIndex] = action.payload;
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateClient.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(deleteClient.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteClient.fulfilled,
        (state, action: PayloadAction<number | undefined>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Клиент удален';
          if (action.payload) {
            const deletedClientIndex = state.clients.findIndex(
              (c) => c.id === action.payload
            );
            if (deletedClientIndex >= 0) {
              state.clients.splice(deletedClientIndex, 1);
            }
            if (action.payload === state.currentClient?.id) {
              state.currentClient = null;
            }
          }
          toast.success(state.statusText);
        }
      )
      .addCase(deleteClient.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      });
  },
});

export const { setClient, setClientIdle } = clientsSlice.actions;

export default clientsSlice.reducer;
